import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { isNullOrUndefined } from '@uzurv/core';
import { ReplaySubject, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'uz-generic-search-select',
  templateUrl: './generic-search-select.component.html',
  styleUrls: ['./generic-search-select.component.scss']
})
export class GenericSearchSelectComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() items: Array<any>;
  @Input() property: string;
  @Input() label: string;
  @Input() selected: any;
  @Input() allowMultiSelect = false;
  @Input() required = false;
  @Input() disabled = false;
  @Input() placeholder: string;

  @Output() selectItem = new EventEmitter<Array<any>>();

  @ViewChild('select', { static: true }) singleSelect: MatSelect;

  filteredItems = new ReplaySubject<Array<any>>(1);
  itemSelector = new UntypedFormControl();
  itemFiltersControl = new UntypedFormControl();

  private _itemSelectorChangeSubscription: Subscription;
  private _itemFiltersChangeSubscription: Subscription;

  constructor() {}

  ngAfterViewInit() {
    this.filteredItems.pipe(take(1)).subscribe(() => {
      // setting the compareWith property to a comparison function
      // triggers initializing the selection according to the initial value of
      // the form control (i.e. _initializeSelection())
      // this needs to be done after the filteredList are loaded initially
      // and after the mat-option elements are available
      this.singleSelect.compareWith = (a: any, b: any) => (a && b ? a[this.property] === b[this.property] : a === b);
    });

    if (this.required) {
      this.itemSelector.setValidators(Validators.required);
    }

    this._itemFiltersChangeSubscription = this.itemFiltersControl.valueChanges.subscribe(() => {
      this.filterItems();
    });

    this._itemSelectorChangeSubscription = this.itemSelector.valueChanges.pipe(filter(value => !isNullOrUndefined(value))).subscribe(() => {
      this.selectItem.emit(this.itemSelector.value);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items']) {
      this.filteredItems.next(this.items.slice());
    }

    if (changes['selected']) {
      if (isNullOrUndefined(changes['selected'].currentValue)) {
        this.itemSelector.reset();
      } else {
        this.itemSelector.setValue(changes['selected'].currentValue, {
          emitEvent: false
        });
      }
    }

    if (changes['disabled']) {
      if (changes['disabled'].currentValue) {
        this.itemSelector.disable();
      } else {
        this.itemSelector.enable();
      }
    }
  }

  ngOnDestroy() {
    if (!isNullOrUndefined(this._itemFiltersChangeSubscription)) {
      this._itemFiltersChangeSubscription.unsubscribe();
    }
    if (!isNullOrUndefined(this._itemSelectorChangeSubscription)) {
      this._itemSelectorChangeSubscription.unsubscribe();
    }
  }

  private filterItems() {
    if (!this.items) {
      return;
    }
    // get the search keyword
    let search = this.itemFiltersControl.value;
    if (!search) {
      this.filteredItems.next(this.items.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the items
    this.filteredItems.next(this.items.filter(item => item[this.property].toLocaleLowerCase().indexOf(search) > -1));
  }
}
